import { useEffect, useRef } from "react";
import { Keyboard } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useI18n } from "@/context/I18nContext";

export const KEYS = [
  ["Espace", "sc.play"], ["I", "sc.start"], ["O", "sc.end"], ["S", "sc.save"], ["Ctrl + E", "sc.export"],
  ["← / →", "sc.seek1"], ["Maj + ← / →", "sc.seek5"], ["M", "sc.mute"], ["P", "sc.final"], ["?", "sc.help"],
];

const typing = (el) => document.querySelector("[role=dialog][data-state=open]") || (el && (el.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName)));
const click = (id) => { const b = document.querySelector(`[data-testid="${id}"]`); if (b && !b.disabled) { b.click(); return true; } return false; };

// Global editor keyboard shortcuts (ignored while typing in a field or when a dialog is open).
export const useEditorShortcuts = (h) => {
  const ref = useRef(h);
  ref.current = h;
  useEffect(() => {
    const onKey = (e) => {
      if (typing(e.target) || e.altKey) return;
      const a = ref.current, k = e.key.toLowerCase(), v = a.vref.current;
      if (e.repeat && !["arrowleft", "arrowright"].includes(k)) return;
      if ((e.ctrlKey || e.metaKey) && k === "e") {
        e.preventDefault();
        if (!click("export-clip-btn")) { a.pickTab("export"); setTimeout(() => click("export-clip-btn"), 150); }
        return;
      }
      if (e.ctrlKey || e.metaKey) return;
      if (e.target?.closest?.("[role=slider], input, textarea, select, [contenteditable=true]")) return;
      const step = e.shiftKey ? 5 : 1;
      const acts = {
        " ": () => v && (v.paused ? v.play().catch(() => {}) : v.pause()),
        i: a.setStart, o: a.setEnd, s: a.save,
        arrowleft: () => v && a.seek(Math.max(0, v.currentTime - step)),
        arrowright: () => v && a.seek(Math.min(a.duration, v.currentTime + step)),
        m: () => click("player-mute-btn"), p: () => click("player-final-preview-btn"),
        "?": () => a.openHelp(),
      };
      const fn = acts[e.key === "?" ? "?" : k];
      if (!fn) return;
      e.preventDefault();
      if (document.activeElement?.tagName === "BUTTON") document.activeElement.blur();
      fn();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
};

export const ShortcutsButton = ({ onClick }) => {
  const { t } = useI18n();
  return (
    <button onClick={onClick} data-testid="shortcuts-help-btn" title={t("sc.title")}
      className="hidden lg:flex cx-ghost rounded-full px-3 py-2 text-[11px] text-slate-300 items-center gap-1.5">
      <Keyboard className="w-3.5 h-3.5" />{t("sc.btn")}
    </button>
  );
};

export const ShortcutsDialog = ({ open, onOpenChange }) => {
  const { t } = useI18n();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0b0e1a] border-white/10 max-w-md" data-testid="shortcuts-dialog">
        <DialogHeader><DialogTitle className="font-display text-white flex items-center gap-2"><Keyboard className="w-4 h-4 text-[#3ad7ff]" />{t("sc.title")}</DialogTitle></DialogHeader>
        <ul className="divide-y divide-white/[0.05]">
          {KEYS.map(([key, label]) => (
            <li key={label} className="flex items-center justify-between py-2.5 text-sm" data-testid={`shortcut-${label.slice(3)}`}>
              <span className="text-slate-300">{t(label)}</span>
              <kbd className="font-mono text-[11px] text-white rounded-md border border-white/15 bg-white/[0.04] px-2 py-1">{key === "Espace" ? t("sc.space") : key === "Maj + ← / →" ? t("sc.shift") : key}</kbd>
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
};
